import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '../../common/Editor';
import PostApi from '../../../api/PostApi';
import { uploadImageFile } from '../../../util/ImageService';
import { getPostBaseUrl, getPostCategoryName, getPostLabels, POST_CLASS, OPTIONS_OF_POST_TYPE, isFree, isUsingContactInfo } from '../../../constant/PostClass';
import UserContext from '../../../context/UserContext';

function hasInvalidCharacters(str) {
    if (!str || str.trim() === '') return false;
  
    // 입력 정규화
    str = str.normalize('NFC');
    
    // 허용된 문자: 한글(가-힣), 자모(ㄱ-ㅣ), 영어, 숫자, 공백, 특수문자(,.!?[]()&~_-'"<>)
    const allowedPattern = /^[가-힣ㄱ-ㅣa-zA-Z0-9 ,.!?\[\]%&~_\-'"<>\/()]*$/;
  
    // 매칭 결과
    const isValid = allowedPattern.test(str);
    return !isValid;
  }

function base64ToFile(base64Data) {
    const [fileInfo, encodingString] = base64Data.split(',');    
    if (!fileInfo || !encodingString) return null;

    const mimeType = fileInfo.split(':')[1].split(';')[0];
    const fileName = `${Date.now()}_image.${mimeType.split('/')[1]}`;
    
    const byteString = atob(encodingString);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
    }

    return new File([intArray], fileName, { type: mimeType });
}


async function uploadImagesAndReplaceInContent(content) {
    const imageTags = content.match(/<img[^>]+src="([^">]+)"/g);
    const base64Images = imageTags ? imageTags.map(tag => tag.match(/src="([^">]+)/)[1]) : [];
    let firstThumbnailUrl = null;

    const uploadedImageUrls = await Promise.all(
        base64Images.map(async (base64, index) => {
            const file = base64ToFile(base64);
            if (!file) return base64;

            const result = await uploadImageFile(file, 'post', 'image', {
                withThumb: index === 0,
            });
            if (!firstThumbnailUrl && result.thumbnailUrl) {
                firstThumbnailUrl = result.thumbnailUrl;
            }
            return result.imageUrl || base64;
        })
    );

    let updatedContent = content;
    base64Images.forEach((base64, index) => {
        updatedContent = updatedContent.replace(base64, uploadedImageUrls[index]);
    });

    return {
        content: updatedContent,
        postThumbnail: firstThumbnailUrl,
    };
};

const getParams = key => {
    const queryParams = new URLSearchParams(window.location.hash.split('?')[1]);
    return queryParams.get(key) || null;
}

const getPostTypeList = isAdmin => {
    const postTypeList = [];
    for (const key in OPTIONS_OF_POST_TYPE) {
        if (OPTIONS_OF_POST_TYPE.hasOwnProperty(key)) {
            const { CATEGORY_NAME, IS_ONLY_ADMIN } = OPTIONS_OF_POST_TYPE[key];
            if (!isAdmin && IS_ONLY_ADMIN) continue;
            postTypeList.push({ value: key, label: CATEGORY_NAME });
        }
    }
    return postTypeList;
}

const getDefaultPostType = () => {
    return getParams('postType') || 'PT001';
}

const getDefaultPostLabel = postType => {
    const labels = getPostLabels(postType || getDefaultPostType());
    return labels.length ? labels[0].value : '';
}

const PostWrite = () => {
    const navigate = useNavigate();
    const { postId = '' } = useParams();
    const { user, updateUserInfo } = useContext(UserContext);
    const [postClass, setPostClass] = useState(POST_CLASS.basic);
    const [postType, setPostType] = useState(getDefaultPostType());
    const [postTitle, setPostTitle] = useState('');
    const [postCompany, setPostCompany] = useState('');
    const [contact, setContact] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactKakao, setContactKakao] = useState('');
    const [content, setContent] = useState('');
    const [label, setLabel] = useState(getDefaultPostLabel());
    const [postTypeList, setPostTypeList] = useState([]);
    const isUpdate = postId && getParams('copy') !== 'true';

    const handleInputChange = (event, setter) => setter(event.target.value);

    const handleTitleInputChange = e => {
        if(e.target.value.length > 50) {
            return alert('제목은 50자 이내로 입력해주세요.');
        }
        setPostTitle(e.target.value);
    }

    const handleEditorChange = (event, editor) => {
        setContent(editor.getData());
    };

    const handleSubmit = async () => {

        if (!postTitle || !postTitle.trim()) return alert('제목이 없습니다.');

        if(hasInvalidCharacters(postTitle)) {
            return alert('제목에 ( ,.!?[]()&~_-\'"<> )를 제외한 이모티콘 및 특수문자를 포함할 수 없습니다.');
        }

        let updatedContent;
        let postThumbnail;
        try {
            const uploadResult = await uploadImagesAndReplaceInContent(content);
            updatedContent = uploadResult.content;
            postThumbnail = uploadResult.postThumbnail;
        } catch (err) {
            console.error(err);
            return alert(err?.message || '이미지 업로드에 실패했습니다.');
        }
        if (!updatedContent) return alert('본문에 내용이 없습니다.');
        
        const newPost = {
            postClass,
            postType,
            postTitle,
            postCompany,
            postContact: contact,
            postContactPhone: contactPhone,
            postContactEmail: contactEmail,
            postContactKakao: contactKakao,
            content: updatedContent,
            postCategory: label,
            postThumbnail,
        }

        let rslt;
        if (isUpdate) rslt = await PostApi.updatePost(postId, newPost);
        else rslt = await PostApi.createPost(newPost);

        if (rslt.code === 'fail') {
            const msg = rslt.message || '작업에 실패하였습니다.';
            // 멤버십/포인트 부족 시 멤버십 가입 화면 유도
            const needsMembership =
                msg.includes('포인트') || msg.includes('멤버쉽') || msg.includes('멤버십');

            if (needsMembership && !isFree(postType)) {
                const confirmed = window.confirm('멤버십과 포인트가 부족하여 글 작성을 할 수 없습니다. 멤버십 가입 화면으로 이동할까요?');
                if (confirmed) navigate('/membership');
                return;
            }

            alert(msg);
        } else {
            updateUserInfo();
            navigate(getPostBaseUrl(postType));
        }
    };

    const handlePostTypeChange = e => {
        const selectedType = e.target.value;
        setPostType(selectedType);
        setLabel(getDefaultPostLabel(selectedType));
    }

    const handleLabelChange = e => {
        const selectedLabel = e.target.value;
        setLabel(selectedLabel);
    }

    
    useEffect(() => {
        if (!postId) return;
        PostApi.getPostFull(postId, true).then(data => {
            if (!data.post) return alert('수정할 권한이 없습니다.');
            const { post } = data;
            setPostTitle(post.post_title);
            setPostCompany(post.post_company);
            setContact(post.post_contact);
            setContactPhone(post.post_contact_phone);
            setContactEmail(post.post_contact_email);
            setContactKakao(post.post_contact_kakao);
            setContent(post.content);
            setPostClass(post.post_class);
            setPostType(post.post_type);
            setLabel(post.post_category);
        });
    }, [])

    useEffect(() => {
        setPostTypeList(getPostTypeList(user.userType === 'admin'));
    }, [user])

    return (
        <div class="vh-content">
            <div class="section-area">
                <div class="section-header">
                    <h1 class="section-header-title">{getPostCategoryName(postType)} {isUpdate ? '글 수정하기' : '글쓰기'}</h1>
                </div>
                { !isUpdate && 
                    <>
                        <div class="section-title">
                            <h1>카테고리</h1>
                        </div>
                        <div class="input-colgroup">
                            <select onChange={handlePostTypeChange} value={postType}>
                                {postTypeList.map(({value, label}) => 
                                    <option key={value} value={value} >
                                        {label}
                                    </option>
                                )}
                            </select>
                        </div>
                    </>
                }
                
                { label && 
                    <>
                        <div class="section-title">
                            <h1>구분</h1>
                        </div>
                        <div class="input-colgroup">
                            <select onChange={handleLabelChange} value={label}>
                                {getPostLabels(postType).map(({value, name}) => 
                                    <option key={value} value={value} >
                                        {name}
                                    </option>
                                )}
                            </select>
                        </div>
                    </>
                }

                <form method="post">
                    <div class="section-title">
                        <h1>제목</h1>
                    </div>
                    <div class="input-colgroup">
                        <input type="" name="" placeholder="제목을 입력해주세요" 
                            value={postTitle}
                            onChange={handleTitleInputChange}
                        />
                    </div>
                    {(false && !isUpdate && !isFree(postType)) && 
                        <>
                        <div class="section-title">
                            <h1>[회원 옵션] 게시물 옵션</h1>
                        </div>
                        <ul class="checkbox-list">
                            <li>
                                <label class="check-radio">
                                    <input 
                                        type="radio" 
                                        name="post-type"
                                        value="default"
                                        checked={postClass === POST_CLASS.basic}
                                        onChange={() => setPostClass(POST_CLASS.basic)}
                                    />
                                    <div class="checkbox-label">
                                        <span>일반 제목</span>
                                        <span class="gray">(포인트 -500차감)</span>
                                        <span class="red">* 매일 한건 무료</span>
                                    </div>
                                </label>
                            </li>
                            <li>
                                <label class="check-radio">
                                    <input
                                        type="radio"
                                        name="post-type"
                                        value="highlight"
                                        checked={postClass === POST_CLASS.bold}
                                        onChange={() => setPostClass(POST_CLASS.bold)}
                                    />
                                    <div class="checkbox-label">
                                        <span class="bold">굵은 제목</span>
                                        <span class="gray">(포인트 -1000차감)</span>
                                    </div>
                                </label>
                            </li>
                            <li>
                                <label class="check-radio">
                                    <input
                                        type="radio"
                                        name="post-type"
                                        value="urgent"
                                        checked={postClass === POST_CLASS.urgent}
                                        onChange={() => setPostClass(POST_CLASS.urgent)}
                                    />
                                    <div class="checkbox-label">
                                        <i class="icon icon-symbol"></i>
                                        <span>아이콘 제목</span>
                                        <span class="gray">(포인트 -2000차감)</span>
                                    </div>
                                </label>
                            </li>
                            <li>
                                <label class="check-radio">
                                    <input
                                        type="radio"
                                        name="post-type"
                                        value="recommend"
                                        checked={postClass === POST_CLASS.pin}
                                        onChange={() => setPostClass(POST_CLASS.pin)}
                                    />
                                    <div class="checkbox-label">
                                        <span class="recommendation">추</span>
                                        <span>추천 등록</span>
                                        <span class="gray">(포인트 -50,000차감, 아이콘, 최상단 7일 노출 홍보)</span>
                                    </div>
                                </label>
                            </li>
                        </ul>
                        </>
                    }
                    <p class="red small">*제목에 특수문자 사용, 카테고리에 맞지 않은 글작성, 공지사항을 위배한 글쓰기는 삭제 및 이용제한되실 수 있습니다.</p>

                    {isUsingContactInfo(postType) && 
                        <>
                            <div class="section-title">
                                <h1>회사 정보</h1>
                            </div>
                            <div class="input-colgroup">
                                
                                <input
                                    type="text"
                                    placeholder="회사명"
                                    value={postCompany}
                                    onChange={(e) => handleInputChange(e, setPostCompany)}
                                />
                                <input
                                    type="text"
                                    placeholder="담당자명"
                                    value={contact}
                                    onChange={(e) => handleInputChange(e, setContact)}
                                />
                                <input
                                    type="text"
                                    placeholder="연락처"
                                    value={contactPhone}
                                    onChange={(e) => handleInputChange(e, setContactPhone)}
                                />
                                <input
                                    type="text"
                                    placeholder="이메일"
                                    value={contactEmail}
                                    onChange={(e) => handleInputChange(e, setContactEmail)}
                                />
                                <input
                                    type="text"
                                    placeholder="카카오톡"
                                    value={contactKakao}
                                    onChange={(e) => handleInputChange(e, setContactKakao)}
                                />
                            </div>
                        </>
                    }

                    <div class="section-title">
                        <h1>내용</h1>
                    </div>
                    <div class="editor-wrap">
                        <Editor data={content} onChange={handleEditorChange} />
                        <div class="btn-area">
                            <button type="button" class="btn btn-primary" onClick={handleSubmit}>
                                {isUpdate ? '수 정' : '등 록'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PostWrite;
export { uploadImagesAndReplaceInContent };
